import * as XLSX from 'xlsx';
import { StaffDailyHours, formatDailyHours } from './staffDailyHours';

// Human-readable labels for backend role strings used in export columns.
// Keep in sync with ROLE_DISPLAY_LABELS in StaffAnalytics.tsx.
const ROLE_DISPLAY_LABELS: Record<string, string> = {
  Physician: 'Physician',
  Nurse: 'Nurse',
  Technician: 'Technician',
  Anesthesiologist: 'Anesthesia',
  AdvancedPracticeProvider: 'Advanced Practice Provider',
};

const formatRoleForExport = (role: string): string => {
  if (!role) return '';
  if (ROLE_DISPLAY_LABELS[role]) return ROLE_DISPLAY_LABELS[role];
  const spaced = role.replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// Types
export interface LocationHistoryEntry {
  date: string;
  staffId: number;
  staffName: string;
  role: string;
  roomId: number | null;
  roomName: string | null;
  roomType: string | null;
  enteredAt: string;
  exitedAt: string | null;
  durationMinutes: number | null;
  hourlyRate: number | null;
  cost: number | null;
}

export interface LocationHistoryExportData {
  dailyHours?: StaffDailyHours | null;
  hospitalTimezone?: string;
  hospitalName: string;
  entries: LocationHistoryEntry[];
  summary: {
    totalEntries: number;
    totalCost: number;
    totalHours: number;
  };
}

export interface LocationHistoryExportResponse {
  staffLocationHistoryExport: LocationHistoryExportData;
}

// Helper functions
export const formatTimestampForExport = (isoString: string | null, timezone?: string): string => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (timezone) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);
    const part = (type: string) => parts.find(p => p.type === type)?.value;
    return `${part('year')}-${part('month')}-${part('day')} ${part('hour')}:${part('minute')}:${part('second')}`;
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const formatDurationForExport = (minutes: number | null): string => {
  if (minutes === null) return '';
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours === 0) return `${remainingMinutes}m`;
  return `${hours}h ${remainingMinutes}m`;
};

const EXPORT_HEADERS = [
  'Hospital Name',
  'Date',
  'Staff Name',
  'Role',
  'Room Name',
  'Room Type',
  'In Time',
  'Out Time',
  'Duration',
  'Duration (min)',
  'Hourly Rate ($)',
  'Cost ($)',
];

const headersForExport = (data: LocationHistoryExportData) =>
  data.dailyHours
    ? [
        ...EXPORT_HEADERS.map(header =>
          header === 'In Time' || header === 'Out Time' ? `${header} (clipped)` : header
        ),
        'Daily Hours',
        'Time Zone',
      ]
    : EXPORT_HEADERS;

const filterColumns = (data: LocationHistoryExportData) =>
  data.dailyHours ? [formatDailyHours(data.dailyHours), data.hospitalTimezone || ''] : [];

const filenameForExport = (data: LocationHistoryExportData, extension: string) => {
  const hours = data.dailyHours
    ? `-hours-${formatDailyHours(data.dailyHours).replace(/:/g, '').replace('–', '-')}`
    : '';
  return `staff-location-history${hours}-${Math.floor(Date.now() / 1000)}.${extension}`;
};

const costForExport = (cost: number): number => Math.round(cost * 100) / 100;

const OFF_SITE_LABEL = 'Off-site';

const normalizeRoomFields = (
  entry: Pick<LocationHistoryEntry, 'roomId' | 'roomName' | 'roomType'>
): { roomName: string; roomType: string } => {
  // If any room metadata is missing, treat it as off-site.
  const isOffSite = entry.roomId === null || entry.roomName === null || entry.roomType === null;
  if (isOffSite) {
    return { roomName: OFF_SITE_LABEL, roomType: OFF_SITE_LABEL };
  }
  return { roomName: entry.roomName || '', roomType: entry.roomType || '' };
};

const mapEntryToRow = (data: LocationHistoryExportData, entry: LocationHistoryEntry) => [
  data.hospitalName,
  entry.date,
  entry.staffName,
  formatRoleForExport(entry.role),
  normalizeRoomFields(entry).roomName,
  normalizeRoomFields(entry).roomType,
  formatTimestampForExport(entry.enteredAt, data.dailyHours ? data.hospitalTimezone : undefined),
  formatTimestampForExport(entry.exitedAt, data.dailyHours ? data.hospitalTimezone : undefined),
  formatDurationForExport(entry.durationMinutes),
  entry.durationMinutes ?? '',
  entry.hourlyRate?.toFixed(2) ?? '',
  entry.cost !== null ? costForExport(entry.cost) : '',
  ...filterColumns(data),
];

// Export functions
export const exportLocationHistoryToCSV = (data: LocationHistoryExportData): void => {
  const rows = data.entries.map(entry => mapEntryToRow(data, entry));

  const worksheet = XLSX.utils.aoa_to_sheet([headersForExport(data), ...rows]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Staff Location History');

  XLSX.writeFile(workbook, filenameForExport(data, 'csv'), { bookType: 'csv' });
};

export const exportLocationHistoryToXLSX = (data: LocationHistoryExportData): void => {
  const rows = data.entries.map(entry => [
    data.hospitalName,
    entry.date,
    entry.staffName,
    formatRoleForExport(entry.role),
    normalizeRoomFields(entry).roomName,
    normalizeRoomFields(entry).roomType,
    formatTimestampForExport(entry.enteredAt, data.dailyHours ? data.hospitalTimezone : undefined),
    formatTimestampForExport(entry.exitedAt, data.dailyHours ? data.hospitalTimezone : undefined),
    formatDurationForExport(entry.durationMinutes),
    entry.durationMinutes ?? '',
    entry.hourlyRate != null ? Number(entry.hourlyRate.toFixed(2)) : '',
    entry.cost !== null ? costForExport(entry.cost) : '',
    ...filterColumns(data),
  ]);

  const worksheet = XLSX.utils.aoa_to_sheet([headersForExport(data), ...rows]);

  // Set column widths
  worksheet['!cols'] = [
    { wch: 20 }, // Hospital Name
    { wch: 12 }, // Date
    { wch: 20 }, // Staff Name
    { wch: 15 }, // Role
    { wch: 18 }, // Room Name
    { wch: 15 }, // Room Type
    { wch: 20 }, // In Time
    { wch: 20 }, // Out Time
    { wch: 10 }, // Duration
    { wch: 14 }, // Duration (min)
    { wch: 14 }, // Hourly Rate
    { wch: 10 }, // Cost
    ...(data.dailyHours ? [{ wch: 18 }, { wch: 25 }] : []),
  ];

  // Add summary sheet
  const summaryHeaders = ['Metric', 'Value'];
  const summaryRows = [
    ['Total Entries', data.summary.totalEntries],
    ['Total Hours', data.summary.totalHours.toFixed(2)],
    ['Total Cost ($)', costForExport(data.summary.totalCost)],
    ...(data.dailyHours
      ? [
          ['Daily Hours', formatDailyHours(data.dailyHours)],
          ['Time Zone', data.hospitalTimezone || ''],
        ]
      : []),
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet([summaryHeaders, ...summaryRows]);
  summarySheet['!cols'] = [{ wch: 15 }, { wch: 15 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Staff Location History');
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  XLSX.writeFile(workbook, filenameForExport(data, 'xlsx'));
};
